/* eslint-disable react/no-danger */
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import Headroom from 'react-headroom';
import {accentFold, stripTags} from "../../helpers/filters";
import KnowledgeCenterArticle from "./KnowledgeCenterArticle";
import KnowledgeCenterFilters from "./KnowledgeCenterFilters";
import './KnowledgeCenter.scss';
import KnowledgeCenterTopicsNav from "./KnowledgeCenterTopicsNav";

class KnowledgeCenter extends Component {

    static propTypes = {
        // eslint-disable-next-line react/forbid-prop-types
        items: PropTypes.array.isRequired
    };

    static getKeywords = items => {
        let keywords = [];

        // Fetch original Drupal-based keyword structure
        // const originalTypeKeywords = items.filter(item => item.keyword).map(item => typeof item.keyword === 'object' ? item.keyword.map(subItem => subItem.trim()) : item.keyword);
        // if (originalTypeKeywords.length) keywords = [...keywords, ...originalTypeKeywords];

        // Fetch new Twill-based keyword structure
        const twillKeywords = items.filter(item => item.tags).map(item => typeof item.tags === 'object' ? item.tags.map(subItem => subItem.name.trim()) : item.tags);
        if (twillKeywords.length) keywords = [...keywords, ...twillKeywords];

        // Flatten and deduplicate the array
        return keywords.flat().filter((value, index, self) => self.indexOf(value) === index);
    }

    state = {
        keywords: [],
        textFilter: '',
        imagesRendered: 0
    };

    componentDidMount() {

        const parsed = queryString.parse(location.search);

        // Keywords
        if (parsed.keyword && !this.state.keywords.length) {
            // eslint-disable-next-line react/no-did-mount-set-state
            this.setState({keywords: parsed.keyword.split(',').map(word => word.trim())});
        }

        // Title
        if (parsed.title && !this.state.textFilter) {
            // eslint-disable-next-line react/no-did-mount-set-state
            this.setState({textFilter: parsed.title});
        }

    }

    clearAll = () => {
        this.setState({
            textFilter: '',
            keywords: []
        });
    };

    /**
     * Filter posts by selected keywords
     *
     * @param post
     * @return {boolean}
     */
    filterKeywords = (post) => {
        if (!this.state.keywords || !this.state.keywords.length) return true;
        if (!post.tags || !post.tags.length) return false;

        return this.state.keywords.every(current => post.tags.map(tag => tag.name.trim()).includes(current));
    };

    /**
     * Filter posts by title
     *
     * @param post
     * @return {boolean}
     */
    filterTitle = (post) => {
        if (!this.state.textFilter) return true;
        if (!post.title) return false;

        return stripTags(accentFold(post.title)).replace(/\W/g, '').toLowerCase().includes(accentFold(this.state.textFilter).replace(/\W/g, '').toLowerCase());
    };

    /**
     * Require Fields
     *
     * @param post
     * @return {boolean}
     */
    filterRequiredFields = (post) => {
        // Require Title
        if (!post.title) return false;

        // Require Date Field or article_date Field
        if ((!post.date || !post.date.value) && !post.article_date) return false;

        return true;
    };

    /**
     * Update State for input by Key
     *
     * @param event
     * @param key
     */
    handleChange = (event, key) => {
        this.setState({[key]: event.target.value});
    };

    handleKeywordChange = (event, keyword) => {
        const keywords = this.state.keywords;

        if (event.target.checked && !keywords.includes(keyword)) {
            keywords.push(keywords);
        } else if (!event.target.checked && keywords.includes(keyword)) {
            keywords.splice(keywords.indexOf(keyword), 1);
        }
        this.setState({keywords})
    };

    handleImageLoaded = () => {
        let imagesCurrentlyRendered = this.state.imagesRendered;

        this.setState({imagesRendered: imagesCurrentlyRendered += 1});
    };

    /**
     * Toggle Keywords for filtering
     *
     * @param event
     * @param keyword
     * @param appendOnly
     */
    toggleKeyword = (event, keyword, appendOnly=false) => {
        event.preventDefault();
        const currentlySelected = this.state.keywords;

        const selectedKeywordIndexInArray = currentlySelected.indexOf(keyword);
        if (!appendOnly && selectedKeywordIndexInArray !== -1) {
            currentlySelected.splice(selectedKeywordIndexInArray, 1);
        } else if (selectedKeywordIndexInArray === -1) {
            currentlySelected.push(keyword);
        }

        this.setState({keywords: currentlySelected});
    };

    render() {
        const {items} = this.props;

        // Get unique array of topics
        const keywords = KnowledgeCenter.getKeywords(items);

        // Filter out invalid posts
        const unFilteredBlogPosts = items
            .filter(this.filterRequiredFields);

        // Apply currently active filters
        const filteredBlogPosts = unFilteredBlogPosts
            .filter(this.filterKeywords)
            .filter(this.filterTitle);

        const filteringActive = filteredBlogPosts.length !== unFilteredBlogPosts.length;
        const numberOfImages = filteredBlogPosts.filter(item => item.supportingImage && item.supportingImage.entity && item.supportingImage.entity.fieldMediaImage && item.supportingImage.entity.fieldMediaImage.url).length;
        const imagesHaveAllRendered = numberOfImages === this.state.imagesRendered;

        // Remove the Featured Post from the array for rendering
        let featuredPost = null;
        if (!filteringActive) {
            featuredPost = filteredBlogPosts.shift();
        }

        return (
            <div className="knowledgeCenter">

                <Headroom 
                    className="knowledgeCenter__subnav"
                    style={{
                        zIndex: '9998',
                    }}
                    // pinStart={93}
                >

                    <KnowledgeCenterTopicsNav/>

                </Headroom>

                {this.state.keywords && this.state.keywords.length ? (
                    <div className="knowledgeCenter__filters container">
                        <div className="knowledgeCenter__filterItems">
                            <p>Filtered By: </p>
                            <div>
                                {this.state.keywords.map(keyword => (
                                    <button className="kcpost__keyword" onClick={() => this.handleKeywordChange({target: {checked: false}}, keyword)}>&times; {keyword}</button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <button className="button button--primary" onClick={() => this.clearAll()}>Clear All</button>
                        </div>
                    </div>
                ) : null}

                <div className="knowledgeCenter__header container">
                    <div className="knowledgeCenter__headerTitle">
                        <h1>Knowledge Center</h1>
                    </div>
                    <KnowledgeCenterFilters textFilter={this.state.textFilter}
                                        keywords={keywords}
                                        keywordsSelected={this.state.keywords}
                                        handleChange={this.handleChange}
                                        toggleKeyword={this.handleKeywordChange}
                                        displayClearAll
                                        clearAll={this.clearAll}/>
                </div>

                {!filteringActive && featuredPost ? (
                    <div className="knowledgeCenter__featured container">
                        <KnowledgeCenterArticle key={`kcpostOuter_${featuredPost.id}`}
                                                post={featuredPost}
                                                toggleKeyword={this.toggleKeyword}
                                                imageLoaded={this.handleImageLoaded}
                                                imagesHaveAllRendered={imagesHaveAllRendered}
                                                isFeatured
                        />
                        <h2 className="knowledgeCenter__nonFeaturedTitle">
                            All Topics
                        </h2>
                    </div>
                ) : null}

                <div className="knowledgeCenter__main container">
                    {filteredBlogPosts && filteredBlogPosts.map((post) => (
                        <KnowledgeCenterArticle key={`kcpostOuter_${post.id}`}
                                                post={post}
                                                toggleKeyword={this.toggleKeyword}
                                                imageLoaded={this.handleImageLoaded}
                                                imagesHaveAllRendered={imagesHaveAllRendered}
                        />
                    ))}
                    {/* If no posts are found, display a message */}
                    {filteredBlogPosts && filteredBlogPosts.length === 0 ? (
                        <div className="knowledgeCenter__noPosts">
                            <h2>No posts found matching that search</h2>
                        </div>
                    ) : null}
                </div>

            </div>
        )
    }
}

export default KnowledgeCenter;



// WEBPACK FOOTER //
// ./frontend/components/KnowledgeCenter/KnowledgeCenter.jsx